import type { MaybeRef } from '@vueuse/core'
import { updateElementalAsAdmin, uploadAttachment, uploadAvatar, uploadImage } from '../api/elementals'
import { useSidebarStore } from '../stores/sidebar'
import { ElementalTypeEnum, getVisibilityEnum } from '../types/elemental'
import type {
  ElementalFormData,
  ElementalType,
  ElementalVisibilityEnum,
} from '../types/elemental'
import { APIError } from '../api/_error/APIError'
import { useCreateElemental } from './mutations/elemetal/useCreateElemental'
import { useUpdateElemental } from './mutations/elemetal/useUpdateElemental'

export const useOnSubmitElemental = (
  elementalTypeRef: MaybeRef<ElementalType | null>,
  isSidebarView: boolean,
  elementalIdRef?: MaybeRef<string | null>,
  ownerId?: MaybeRef<string | null | undefined>,
  options: Partial<{
    goToEdit?: boolean
    justSave?: boolean
  }> = {},
) => {
  const { mutateAsync: createElemental, isPending: isPendingCreate }
    = useCreateElemental()
  const { mutateAsync: updateElemental, isPending: isPendingUpdate }
    = useUpdateElemental()
  const { goToView } = useSidebarStore()
  const router = useRouter()
  const toast = useToast()
  const user = useUser()

  async function onSubmit(values: ElementalFormData) {
    const elementalId = unref(elementalIdRef)
    const elementalType = unref(elementalTypeRef)
    const isAdminMode = computed(
      () => user.value?.id !== ownerId && user.value?.isAdmin,
    )

    try {
      if (!elementalType) {
        toast.error('Elemental type not found')
        return
      }
      if (elementalType.id === ElementalTypeEnum.Table) {
        values.rows = undefined
        values.columns = undefined
      }

      const enterprise = useEnterprise()
      const availableKeys = Object.keys(elementalType?.metadata.layout || {})
      const availableFields = [] as (keyof ElementalFormData)[]
      for (const key of availableKeys) {
        if (elementalType?.metadata.layout[key] !== 'none')
          availableFields.push(key as keyof ElementalFormData)
      }
      const dataToSubmit = values
      // Prepare metadate
      if (dataToSubmit.metadata)
        dataToSubmit.metadata = JSON.stringify(dataToSubmit.metadata ?? {})

      // Prepare visibility
      if (values.attachmentsVisibility !== undefined) {
        dataToSubmit.attachmentsVisibility = getVisibilityEnum(
          values.attachmentsVisibility as keyof typeof ElementalVisibilityEnum,
        )
        dataToSubmit.elementalsVisibility = getVisibilityEnum(
          values.elementalsVisibility as keyof typeof ElementalVisibilityEnum,
        )
      }

      dataToSubmit.enterpriseId = enterprise.value

      if (
        dataToSubmit.isPremium
        && dataToSubmit.price
        && dataToSubmit.isFixedPrice
      ) {
        dataToSubmit.price = Math.round(dataToSubmit.price * 100)
        if (dataToSubmit.priceOriginal && dataToSubmit.priceOriginal > 0)
          dataToSubmit.priceOriginal = Math.round(dataToSubmit.priceOriginal * 100)
        else dataToSubmit.priceOriginal = null
      }
      else {
        dataToSubmit.price = undefined
        dataToSubmit.priceOriginal = undefined
      }

      sendGTMEvent({
        event: `${elementalType.name}-${elementalId ? 'update' : 'create'}`,
      })

      let record = null
      if (elementalId) {
        if (isAdminMode.value) {
          record = await updateElementalAsAdmin(
            elementalId,
            dataToSubmit,
          )
        }
        else {
          record = await updateElemental({
            elemental: dataToSubmit,
            elementalType: getElementalType(elementalType.id),
            id: elementalId,
          })
        }
      }
      else {
        record = await createElemental({
          elemental: dataToSubmit,
          elementalType: getElementalType(elementalType.id),
        })
      }

      if (!record) {
        toast.error(
          `${capitalizeFirstLetter(elementalType?.name)} update failed`,
        )
        return
      }

      const id = record.id || elementalId!
      const url = record.url || id

      if (values.avatar && typeof values.avatar !== 'string') {
        const result = await uploadAvatar(
          id,
          values.avatar,
          isAdminMode.value,
        )

        if (!result)
          toast.error('Avatar upload failed')
      }

      if (values.coverImages?.length) {
        await Promise.all(
          values.coverImages.map(async (image: File) => {
            const result = await uploadImage(
              id,
              image,
              'cover',
              isAdminMode.value,
            )

            if (!result)
              toast.error(`Image upload failed: ${image.name}`)
          }),
        )
      }

      if (values.outputImages?.length) {
        await Promise.all(
          values.outputImages.map(async (image: File) => {
            const result = await uploadImage(
              id,
              image,
              'output',
              isAdminMode.value,
            )

            if (!result)
              toast.error(`Image upload failed: ${image.name}`)
          }),
        )
      }

      if (values.attachments?.length) {
        await Promise.all(
          values.attachments.map(async (attachment: File) => {
            const result = await uploadAttachment(
              id,
              attachment,
              isAdminMode.value,
            )

            if (!result)
              toast.error(`Attachment upload failed: ${attachment.name}`)
          }),
        )
      }

      if (record) {
        if (options.justSave || elementalType.id === ElementalTypeEnum.Table)
          return record
        await loadElementalLists(id, dataToSubmit.isPremium, elementalType?.id)
        if (isSidebarView)
          return goToView()
        return record
      }
    }
    catch (error) {
      console.error(error)
      if (error instanceof APIError) {
        if (error.message === 'Topics length is invalid.')
          toast.error('You must select at least one topic.')
        else
          toast.error(error.message)
      }
      else {
        toast.error(
          `${capitalizeFirstLetter(elementalType?.name)} update failed`,
        )
      }
    }
  }

  return {
    onSubmit,
    isPendingCreate,
    isPendingUpdate,
  }
}
